import {
  FiDatabase,
  FiTag,
  FiGrid,
  FiToggleRight,
  FiCloudLightning,
  FiImage,
  FiLayers,
  FiCpu,
  FiTarget,
} from "react-icons/fi";
import {
  SelectField,
  ToggleField,
  TagsField,
  SegmentedControlField,
} from "./JobField";

// Job type registry with configurations for each job type
export const JOB_REGISTRY = {
  // OCR Workflow Job
  "/ocr/ingest": {
    title: "Unstructured Data Synchronization Job",
    icon: <FiImage className="w-5 h-5" />,
    getInitialValues: (requestBody) => ({
      data_connector_name: requestBody?.data_connector_name || "",
      use_llm: requestBody?.use_llm || false,
    }),
    fields: [
      {
        id: "data_connector_name",
        label: "Data Connector",
        icon: <FiDatabase />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { vdbProfiles } = job?.context || {};
          const unstructuredVDBProfiles = Object.values(vdbProfiles || {})
            .filter(
              (profile) =>
                profile.type === "S3DataSourceManager" ||
                profile.type === "SharepointDataSourceManager",
            )
            .map((profile) => profile.name);

          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Connector"
              options={unstructuredVDBProfiles.map((profile) => ({
                value: profile,
                label: profile,
              }))}
            />
          );
        },
      },
      {
        id: "use_llm",
        label: "Enhanced OCR with LLM",
        icon: <FiToggleRight />,
        renderField: ({ id, value, onChange, isDisabled }) => (
          <ToggleField
            id={id}
            value={value}
            onChange={onChange}
            isDisabled={isDisabled}
            labelOn="Enhanced OCR enabled"
            labelOff="Standard OCR only"
          />
        ),
      },
    ],
  },

  // Prepare Data Job
  "/prepare_data": {
    title: "Data Preparation Job",
    icon: <FiCloudLightning className="w-5 h-5" />,
    getInitialValues: (requestBody) => ({
      data_connector_name: requestBody?.data_connector_name || "",
      llm_profile_name: requestBody?.llm_profile_name || "Deasy Compute",
    }),
    fields: [
      {
        id: "data_connector_name",
        label: "Data Connector",
        icon: <FiDatabase />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { vdbProfiles } = job?.context || {};
          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Connector"
              options={Object.keys(vdbProfiles || {}).map((profile) => ({
                value: profile,
                label: profile,
              }))}
            />
          );
        },
      },
      {
        id: "llm_profile_name",
        label: "LLM Profile",
        icon: <FiCpu />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { llmProfiles } = job?.context || {};
          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select LLM Profile"
              options={[
                { value: "Deasy Compute", label: "Deasy Compute" },
                ...(Object.keys(llmProfiles || {}).map((profile) => ({
                  value: profile,
                  label: profile,
                })) || []),
              ]}
            />
          );
        },
      },
    ],
  },

  // Classification Job
  "/classify_bulk": {
    title: "Classification Job",
    icon: <FiCloudLightning className="w-5 h-5" />,
    getInitialValues: (requestBody) => ({
      data_connector_name: requestBody?.data_connector_name || "",
      llm_profile_name: requestBody?.llm_profile_name || "Deasy Compute",
      hierarchy_name: requestBody?.hierarchy_name || "",
      tag_names: requestBody?.tag_names || [],
      classification_mode: requestBody?.hierarchy_name ? "schema" : "tags",
    }),
    fields: [
      {
        id: "data_connector_name",
        label: "Data Connector",
        icon: <FiDatabase />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { vdbProfiles } = job?.context || {};
          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Connector"
              options={Object.keys(vdbProfiles || {}).map((profile) => ({
                value: profile,
                label: profile,
              }))}
            />
          );
        },
      },
      {
        id: "llm_profile_name",
        label: "LLM Profile",
        icon: <FiCpu />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { llmProfiles } = job?.context || {};
          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select LLM Profile"
              options={[
                { value: "Deasy Compute", label: "Deasy Compute" },
                ...(Object.keys(llmProfiles || {}).map((profile) => ({
                  value: profile,
                  label: profile,
                })) || []),
              ]}
            />
          );
        },
      },
      {
        id: "classification_mode",
        label: "Classification Type",
        icon: <FiTag />,
        renderField: ({ id, value, onChange, isDisabled }) => (
          <SegmentedControlField
            id={id}
            value={value}
            onChange={onChange}
            isDisabled={isDisabled}
            options={[
              {
                value: "tags",
                label: "Tags",
                icon: <FiTag className="w-4 h-4" />,
              },
              {
                value: "schema",
                label: "Schema",
                icon: <FiGrid className="w-4 h-4" />,
              },
            ]}
          />
        ),
      },
    ],
    // Additional UI rendering for classification-specific components
    renderAdditional: ({ fieldValues, setFieldValues, isDisabled, job }) => {
      const { graphs, savedTags } = job?.context || {};

      if (fieldValues.classification_mode === "schema") {
        return (
          <div className="mt-4 group/field transition-all duration-200">
            <label className="block text-sm font-medium text-gray-600 mb-2 group-hover/field:text-primary transition-colors duration-200 flex items-center">
              <FiGrid className="mr-1.5 text-primary/70" />
              Schema Selection
            </label>
            <SelectField
              id={`hierarchy-${job?.id}`}
              value={fieldValues.hierarchy_name}
              onChange={(value) =>
                setFieldValues({
                  ...fieldValues,
                  hierarchy_name: value,
                  tag_names: [],
                })
              }
              isDisabled={isDisabled}
              placeholder="Select Schema"
              options={(graphs || []).map((graph) => ({
                value: graph.graph_name,
                label: graph.graph_name,
              }))}
            />
          </div>
        );
      } else {
        return (
          <div className="mt-4 group/field transition-all duration-200">
            <label className="block text-sm font-medium text-gray-600 mb-2 group-hover/field:text-primary transition-colors duration-200 flex items-center">
              <FiTag className="mr-1.5 text-primary/70" />
              Tags Selection
            </label>
            <TagsField
              id={`tags-${job?.id}`}
              value={fieldValues.tag_names}
              onChange={(value) =>
                setFieldValues({
                  ...fieldValues,
                  hierarchy_name: "",
                  tag_names: value,
                })
              }
              isDisabled={isDisabled}
              availableTags={(savedTags || []).map((tag) => ({
                value: tag.name,
                label: tag.name,
              }))}
            />
          </div>
        );
      }
    },
  },

  // Export Workflow Job
  "/dataslice/export/metadata/destination": {
    title: "Export Metadata Job",
    icon: <FiCloudLightning className="w-5 h-5" />,
    getInitialValues: (requestBody) => ({
      dataslice_id: requestBody?.dataslice_id || "",
      data_connector_name: requestBody?.data_connector_name || "",
      destination_name: requestBody?.destination_name || "",
      export_tags: requestBody?.export_tags || [],
      export_level: requestBody?.export_level || "BOTH",
    }),
    fields: [
      {
        id: "data_connector_name",
        label: "Data Connector",
        icon: <FiDatabase />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { vdbProfiles } = job?.context || {};
          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Connector"
              options={Object.keys(vdbProfiles || {}).map((profile) => ({
                value: profile,
                label: profile,
              }))}
            />
          );
        },
      },
      {
        id: "dataslice_id",
        label: "Data Slice ID",
        icon: <FiTarget />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { dataSlices } = job?.context || {};
          const { data_connector_name } = job?.endpoint_request_body || {};

          const filteredDataSlices = (dataSlices || []).filter((slice) => {
            let sliceCollection = slice?.vdb_collection_name;
            if (!sliceCollection) {
              sliceCollection = slice?.vector_db_config?.name;
            }
            return sliceCollection === data_connector_name;
          });

          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Data Slice"
              options={filteredDataSlices.map((slice) => ({
                value: slice?.name,
                label: slice?.name,
              }))}
            />
          );
        },
      },
      {
        id: "destination_name",
        label: "Destination Name",
        icon: <FiDatabase />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { deasyUserConfig } = job?.context || {};
          const destinationProfiles = Object.keys(
            deasyUserConfig?.destinationConfig?.Configs || {},
          );

          return (
            <SelectField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              placeholder="Select Destination"
              options={destinationProfiles.map((profile) => ({
                value: profile,
                label: profile,
              }))}
            />
          );
        },
      },
      {
        id: "export_level",
        label: "Export Level",
        icon: <FiLayers />,
        renderField: ({ id, value, onChange, isDisabled }) => (
          <SelectField
            id={id}
            value={value}
            onChange={onChange}
            isDisabled={isDisabled}
            options={[
              { value: "both", label: "Both File and Chunk" },
              { value: "chunk", label: "Chunk Level" },
            ]}
          />
        ),
      },
      {
        id: "export_tags",
        label: "Tags Selection",
        icon: <FiTag />,
        renderField: ({ id, value, onChange, isDisabled, job }) => {
          const { savedTags } = job?.context || {};

          return (
            <TagsField
              id={id}
              value={value}
              onChange={onChange}
              isDisabled={isDisabled}
              availableTags={(savedTags || []).map((tag) => ({
                value: tag.name,
                label: tag.name,
              }))}
            />
          );
        },
      },
    ],
  },
};
